import {
  convertUsdMinorToVesMinor,
  lineTotalMinor,
  sumMinor,
  type MinorUnits,
  type Sku,
} from '@lombok/shared';

/**
 * Pure cart math for the "Crear pedido" screen. The backend is authoritative on
 * pricing and totals (it snapshots SKU prices and locks the rate) — this is only
 * a live client-side PREVIEW so the user sees a USD subtotal and its VES
 * equivalent before submitting. All amounts are integer minor units (no floats).
 */

/** One line in the draft cart: a chosen SKU + a whole-unit quantity. */
export interface CartLine {
  sku: Sku;
  quantity: number;
}

/** A line's preview total in USD minor units (unit base price × quantity). */
export function cartLineTotalMinorUsd(line: CartLine): MinorUnits {
  return lineTotalMinor(line.sku.basePriceMinorUsd, line.quantity);
}

/**
 * Sum of all valid lines in USD minor units. Lines with a non-positive quantity
 * contribute zero (they are ignored on submit too), so a half-filled form still
 * previews a sensible subtotal.
 */
export function cartSubtotalMinorUsd(lines: readonly CartLine[]): MinorUnits {
  return sumMinor(
    lines.map((l) => (l.quantity > 0 ? cartLineTotalMinorUsd(l) : 0)),
  );
}

/**
 * The cart subtotal converted to VES minor units at the given rate (VES per 1
 * USD). A non-positive/NaN rate yields 0 — the UI requires a valid rate before
 * showing a VES figure or allowing submit.
 */
export function cartSubtotalMinorVes(lines: readonly CartLine[], rate: number): MinorUnits {
  if (!Number.isFinite(rate) || rate <= 0) return 0;
  return convertUsdMinorToVesMinor(cartSubtotalMinorUsd(lines), rate);
}

/** Total whole-unit count across the cart (for a compact "N artículos" summary). */
export function cartItemCount(lines: readonly CartLine[]): number {
  return lines.reduce((acc, l) => acc + (l.quantity > 0 ? Math.trunc(l.quantity) : 0), 0);
}
